import React from "react";
import { Link } from "react-router-dom";
import { Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import logo from "@/assets/360view-logo.jpg";
import { useBusinessSettings } from "@/context/SettingsContext";

export default function Footer() {
  const s = useBusinessSettings();
  return (
    <footer data-testid="site-footer" className="bg-obsidian border-t border-chrome mt-20">
      <div className="container-x py-16">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-white p-1.5 shadow-lg shadow-black/40">
                <img src={logo} alt="360 View LLC logo" className="h-14 w-auto block" />
              </div>
              <div>
                <div className="font-display font-black text-white text-lg tracking-wide">360 VIEW LLC</div>
                <div className="eyebrow text-[10px] text-solar">Solar Surveillance · NV & UT</div>
              </div>
            </div>
            <p className="text-white/70 text-sm max-w-md mb-6">
              Rapid-deployment solar surveillance for construction, commercial, government and remote sites across Nevada and Utah.
            </p>
            <div className="space-y-2 text-sm text-white/80">
              <a href={`tel:${(s.phone || "").replace(/[^0-9+]/g,'')}`} data-testid="footer-phone" className="flex items-center gap-2 hover:text-solar transition-colors">
                <Phone size={16} weight="fill" className="text-solar" /> {s.phone}
              </a>
              <a href={`mailto:${s.email}`} data-testid="footer-email" className="flex items-center gap-2 hover:text-solar transition-colors">
                <EnvelopeSimple size={16} weight="fill" className="text-solar" /> {s.email}
              </a>
              <div className="flex items-center gap-2">
                <MapPin size={16} weight="fill" className="text-solar" /> {s.headquarters}
              </div>
            </div>
          </div>

          <FooterCol title="Systems" links={[
            { to: "/systems/mini-solar-pole-system", label: "Mini Solar Pole" },
            { to: "/systems/standard-surveillance-trailer", label: "Standard Trailer" },
            { to: "/systems/pro-surveillance-trailer", label: "Pro Trailer" },
            { to: "/systems/compare", label: "Compare Systems" },
          ]}/>

          <FooterCol title="Industries" links={[
            { to: "/industries/construction-site-security", label: "Construction" },
            { to: "/industries/commercial-property-security", label: "Commercial" },
            { to: "/industries/equipment-yard-security", label: "Equipment Yards" },
            { to: "/industries/government-municipal-security", label: "Government" },
            { to: "/industries", label: "All Industries" },
          ]}/>

          <FooterCol title="Company" links={[
            { to: "/about", label: "About" },
            { to: "/service-areas/nevada", label: "Nevada" },
            { to: "/service-areas/utah", label: "Utah" },
            { to: "/resources", label: "Learning Center" },
            { to: "/case-studies", label: "Case Studies" },
            { to: "/faq", label: "FAQ" },
            { to: "/contact", label: "Contact" },
          ]}/>
        </div>

        <div className="mt-14 pt-8 border-t border-chrome flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            <span className="eyebrow text-solar border border-solar/40 px-2.5 py-1">NDOT SBE Certified</span>
            <span className="eyebrow text-solar border border-solar/40 px-2.5 py-1">Nevada CCTV Approved</span>
          </div>
          <div className="text-xs text-white/50 space-x-4">
            <span>© {new Date().getFullYear()} 360 View LLC</span>
            <Link to="/privacy-policy" className="hover:text-solar">Privacy</Link>
            <Link to="/terms-of-use" className="hover:text-solar">Terms</Link>
            <Link to="/accessibility" className="hover:text-solar">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="eyebrow text-solar mb-4">{title}</div>
      <ul className="space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="text-sm text-white/75 hover:text-solar transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
